import React, { useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../context/authContext';
import { useNavigate, useSearchParams } from "react-router-dom";
import { RemoveRedEye, Report } from '@mui/icons-material';
import { Box, Button, Card, Checkbox, FormControl, FormHelperText, FormLabel, IconButton, Input, LinearProgress, Link, Snackbar, Stack, Typography } from '@mui/joy';

import LoadingSpinnerComponent from '../components/LoadingSpinnerComponent';
import logo from '../resources/logo.svg'

//@ts-ignore
import '../styles/authentication.scss';

function ClientManagementPage() {
    const { signup } = useAuth();
    let navigate = useNavigate();

    const [parameters] = useSearchParams();
    const [value, setValue] = React.useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [success, setSuccess] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const minLength = 8;
    let strengthLevel = 0;
    const plan = parameters.get('plan')
    const token = parameters.get('token')

    var businessName: string = '';
    var prefilledEmail: string = '';
    var subscriptionId: string = '';

    if (token) {
        const decoded: any = jwtDecode<any>(token);
        prefilledEmail = decoded.email;
        businessName = decoded.businessName;
        subscriptionId = decoded.subscriptionId;
    }

    const isUpperCase = /[A-Z]/.test(value);
    const isLowerCase = /[a-z]/.test(value);
    const isNumber = /[0-9]/.test(value);
    const isSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    const isLengthValid = value.length >= minLength;  // Minimum length validation

    if (isUpperCase) strengthLevel++;
    if (isLowerCase) strengthLevel++;
    if (isNumber) strengthLevel++;
    if (isSpecialChar) strengthLevel++;
    if (isLengthValid) strengthLevel++;

    const saveNewSubscription = async (event: any) => {
        event.preventDefault();

        if (strengthLevel < 4) {
            setError('Password is too weak. Please use a stronger password');
            setShowError(true);
            return;
        }

        setLoading(true);

        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries(formData.entries());

        try {
            await signup(formJson.name.toString(), formJson.businessName && formJson.businessName.toString(), formJson.email.toString(), formJson.password.toString(), plan ?? "A", subscriptionId, token).then(() => {
                setShowSuccess(true);
                setSuccess('Your account has been created successfully');

                navigate('/apps-list');
            }).catch((error: any) => {
                setError(error.message);
                setShowError(true);
                setLoading(false);
            });

        } catch (error: any) {
            setError(error.message);
            setShowError(true);
            setLoading(false);
        }
    }

    return (
        <>
            {showError &&
                <Snackbar
                    variant="soft"
                    color="danger"
                    open={showError}
                    onClose={() => setShowError(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowError(false)}
                            size="sm"
                            variant="soft"
                            color="danger"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {error}
                </Snackbar>
            }
            {showSuccess &&
                <Snackbar
                    variant="soft"
                    color="success"
                    open={showSuccess}
                    onClose={() => setShowSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowSuccess(false)}
                            size="sm"
                            variant="soft"
                            color="danger"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {success}
                </Snackbar>
            }
            <div className='container-fluid bg-cloudie p-5'>
                <div className='text-center'>
                    <a
                        href='/'>
                        <img
                            src={logo}
                            height={40 + 'px'}
                            alt='Appinion logo' />
                    </a>
                </div>
                <div className='mt-4 text-center'>
                    {token &&
                        <>
                            <Typography
                                level='body-lg'
                                sx={{
                                    marginBottom: 2
                                }}>
                                <span>
                                    <Typography
                                        level="body-lg"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: '#313131',
                                            marginBottom: 1
                                        }}>
                                        {businessName + ' '}
                                    </Typography>
                                </span>
                                invited you to join their team. Sign up to get started
                            </Typography>
                        </>
                    }
                    <Typography
                        level="title-lg"
                        sx={{
                            fontWeight: 'bold',
                            color: '#313131',
                            marginBottom: 1
                        }}>
                        Sign up
                    </Typography>
                    {!token &&
                        <Typography
                            level="body-sm"
                            component="h1">
                            Already a client?
                            <a href='/authenticate'>
                                <span className='underlined'> Sign in</span>
                            </a>
                        </Typography>
                    }
                </div>
                <Box
                    component="section"
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    sx={{

                    }}>
                    <Card
                        sx={{
                            mx: 'auto', // margin left & right
                            my: 4, // margin top & bottom
                            py: 5, // padding top & bottom
                            px: 5, // padding left & right
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            borderRadius: 'sm',
                            boxShadow: 'md',
                            maxWidth: 600
                        }}
                        variant="outlined"
                    >
                        {loading ?
                            <LoadingSpinnerComponent />
                            :
                            <form
                                onSubmit={(event) => {
                                    saveNewSubscription(event)
                                }}
                            >
                                <Stack spacing={4}>
                                    <FormControl>
                                        <FormLabel>Name</FormLabel>
                                        <Input
                                            // html input attribute
                                            name="name"
                                            type="text"
                                            size='md'
                                            required
                                        />
                                    </FormControl>
                                    {!token &&
                                        <FormControl>
                                            <FormLabel>Business name</FormLabel>
                                            <Input
                                                // html input attribute
                                                name="businessName"
                                                type="text"
                                                size='md'
                                            />
                                        </FormControl>
                                    }
                                    <FormControl>
                                        <FormLabel>Email</FormLabel>
                                        <Input
                                            // html input attribute
                                            name="email"
                                            type="email"
                                            required
                                            size='md'
                                            defaultValue={prefilledEmail ?? ''}
                                        />
                                    </FormControl>
                                    <Stack
                                        spacing={0.5}
                                        sx={{ '--hue': Math.min(strengthLevel * 10, 120) }}>
                                        <FormControl>
                                            <FormLabel>Password</FormLabel>
                                            <Input
                                                name="password"
                                                type={showPass ? "text" : "password"}
                                                endDecorator={
                                                    <IconButton
                                                        onClick={() => setShowPass(!showPass)}>
                                                        {
                                                            showPass ?
                                                                <RemoveRedEye></RemoveRedEye>
                                                                :
                                                                <RemoveRedEye></RemoveRedEye>
                                                        }
                                                    </IconButton>
                                                }
                                                value={value}
                                                onChange={(event) => setValue(event.target.value)}
                                            />
                                            <LinearProgress
                                                determinate
                                                size="sm"
                                                value={Math.min((strengthLevel * 100) / 5, 100)}
                                                sx={{
                                                    bgcolor: 'background.level3',
                                                    color: 'hsl(var(--hue) 80% 40%)',
                                                    marginTop: 2
                                                }}
                                            />
                                            <Typography
                                                level="body-xs"
                                                sx={{ alignSelf: 'flex-end', color: 'hsl(var(--hue) 80% 30%)' }}
                                            >
                                                {strengthLevel < 1 && 'Very weak'}
                                                {strengthLevel >= 1 && strengthLevel < 3 && 'Weak'}
                                                {strengthLevel >= 3 && strengthLevel < 4 && 'Medium'}
                                                {strengthLevel >= 4 && strengthLevel < 5 && 'Strong'}
                                                {strengthLevel >= 5 && 'Very strong'}
                                            </Typography>
                                        </FormControl>
                                    </Stack>

                                    <FormControl>
                                        <Checkbox
                                            label={
                                                <React.Fragment>
                                                    I have read and agree to the{' '}
                                                    <Typography sx={{ fontWeight: 'sm' }}>terms and conditions</Typography>.
                                                </React.Fragment>
                                            }
                                            required
                                        />
                                        <FormHelperText>
                                            <Typography level="body-sm">
                                                Read our <Link href="terms">terms and conditions</Link>.
                                            </Typography>
                                        </FormHelperText>
                                    </FormControl>
                                    <Button
                                        type='submit'
                                        sx={{ mt: 1 /* margin top */ }}>
                                        Start now
                                    </Button>
                                </Stack>
                            </form>
                        }
                    </Card>
                </Box>
            </div >
        </>
    );
};

export default ClientManagementPage;